import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation,useNavigate, useParams } from 'react-router-dom';

import { paths } from '@config/paths';

import { currentInventoryListAtom, currentPageAtom, InventoryTab, itemsPerPageAtom } from '../atoms/inventoryAtoms';

/**
 * Custom hook for inventory-related navigation
 * Encapsulates the logic for navigating to inventory details and closing the drawer
 *
 * @example
 * ```tsx
 * const { openInventoryDetails, closeInventoryDetails, switchInventoryDetailsTab } = useInventoryNavigation();
 *
 * // Open inventory details to a specific tab
 * openInventoryDetails('inventory-123', InventoryTab.VEHICLE_DETAILS);
 *
 * // Close the drawer
 * closeInventoryDetails();
 * ```
 */
export function useInventoryNavigation() {
	const navigate = useNavigate();
	const location = useLocation();
	const { servicesProviderId, inventoryItemId } = useParams<{
		servicesProviderId: string;
		inventoryItemId?: string;
	}>();

	// Get current inventory list and pagination state
	const currentInventoryList = useAtomValue(currentInventoryListAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);

	// Track pending navigation after page change
	const pendingNavigationRef = useRef<{
		action: 'next' | 'prev';
		targetTab: InventoryTab;
	} | null>(null);

	/**
	 * Navigate to inventory details with a specific tab
	 * @param itemId - The ID of the inventory item to open
	 * @param tab - The tab to open (defaults to VEHICLE_DETAILS)
	 */
	const openInventoryDetails = useCallback(
		(itemId: string, tab: InventoryTab = InventoryTab.VEHICLE_DETAILS) => {
			if (!servicesProviderId) {
				console.warn('Cannot navigate to inventory details: missing servicesProviderId');
				return;
			}

			// Preserve query parameters (filters, pagination, etc.) when opening details
			const path = paths.inventoryDetails.getHref(servicesProviderId, itemId, tab);
			navigate(path + location.search);
		},
		[navigate, servicesProviderId, location.search],
	);

	/**
	 * Close the inventory details drawer and return to the inventory list
	 */
	const closeInventoryDetails = () => {
		if (!servicesProviderId) {
			console.warn('Cannot close inventory details: missing servicesProviderId');
			return;
		}

		// Preserve query parameters (filters, pagination, etc.) when closing details
		const path = paths.inventory.getHref(servicesProviderId);
		navigate(path + location.search);
	};

	/**
	 * Change the active tab in the inventory details drawer
	 * @param itemId - The ID of the currently open inventory item
	 * @param tab - The new tab to switch to
	 */
	const switchInventoryDetailsTab = (itemId: string, tab: InventoryTab) => {
		if (!servicesProviderId) {
			console.warn('Cannot switch tab: missing servicesProviderId');
			return;
		}

		// Preserve query parameters (filters, pagination, etc.) when switching tabs
		const path = paths.inventoryDetails.getHref(servicesProviderId, itemId, tab);
		navigate(path + location.search);
	};

	/**
	 * Navigate to the previous inventory item in the list
	 * Handles pagination automatically if needed
	 * @param currentItemId - The ID of the currently displayed inventory item
	 * @param tab - The tab to maintain when navigating (defaults to VEHICLE_DETAILS)
	 */
	const navigateToPreviousItem = (
		currentItemId: string,
		tab: InventoryTab = InventoryTab.VEHICLE_DETAILS,
	) => {
		const currentIndex = currentInventoryList.indexOf(currentItemId);

		if (currentIndex > 0) {
			// Previous item is on current page
			const prevItemId = currentInventoryList[currentIndex - 1];
			openInventoryDetails(prevItemId, tab);
		} else if (currentIndex === 0 && currentPage > 1) {
			// Need to go to previous page - go to last item of previous page
			pendingNavigationRef.current = { action: 'prev', targetTab: tab };
			setCurrentPage(currentPage - 1);
		}
		// If at the beginning of first page, do nothing
	};

	/**
	 * Navigate to the next inventory item in the list
	 * Handles pagination automatically if needed
	 * @param currentItemId - The ID of the currently displayed inventory item
	 * @param tab - The tab to maintain when navigating (defaults to VEHICLE_DETAILS)
	 */
	const navigateToNextItem = (
		currentItemId: string,
		tab: InventoryTab = InventoryTab.VEHICLE_DETAILS,
	) => {
		const currentIndex = currentInventoryList.indexOf(currentItemId);

		if (currentIndex >= 0 && currentIndex < currentInventoryList.length - 1) {
			// Next item is on current page
			const nextItemId = currentInventoryList[currentIndex + 1];
			openInventoryDetails(nextItemId, tab);
		} else if (currentIndex === currentInventoryList.length - 1) {
			// Need to go to next page - go to first item of next page
			pendingNavigationRef.current = { action: 'next', targetTab: tab };
			setCurrentPage(currentPage + 1);
		}
		// If at the end of results, do nothing (boundary check should disable button)
	};

	/**
	 * Get navigation state for the current inventory item
	 * Returns information about whether prev/next navigation is possible
	 */
	const getNavigationState = (currentItemId: string, totalCount: number) => {
		const currentIndex = currentInventoryList.indexOf(currentItemId);
		const isFirstOnPage = currentIndex === 0;
		const isLastOnPage = currentIndex === currentInventoryList.length - 1;
		const isFirstPage = currentPage === 1;
		const totalPages = Math.ceil(totalCount / itemsPerPage);
		const isLastPage = currentPage >= totalPages;

		return {
			canGoPrevious: !(isFirstOnPage && isFirstPage),
			canGoNext: !(isLastOnPage && isLastPage),
			currentIndex,
			totalInCurrentPage: currentInventoryList.length,
		};
	};

	// Effect to handle navigation after page change
	useEffect(() => {
		if (pendingNavigationRef.current && currentInventoryList.length > 0) {
			const { action, targetTab } = pendingNavigationRef.current;

			if (action === 'next') {
				// Navigate to first item of new page
				openInventoryDetails(currentInventoryList[0], targetTab);
			} else if (action === 'prev') {
				// Navigate to last item of new page
				openInventoryDetails(currentInventoryList[currentInventoryList.length - 1], targetTab);
			}

			// Clear pending navigation
			pendingNavigationRef.current = null;
		}
	}, [currentInventoryList, openInventoryDetails]);

	return {
		openInventoryDetails,
		closeInventoryDetails,
		switchInventoryDetailsTab,
		navigateToPreviousItem,
		navigateToNextItem,
		getNavigationState,
		// Expose params in case components need them
		servicesProviderId,
		inventoryItemId,
	};
}
